import { Injectable } from '@angular/core';
import { FeatureFlagsDataService } from './../data-services/feature-flags-data.service';

import { BehaviorSubject, Observable, firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Feature, FeatureFlag } from '../../models/feature-flags';
import { FeatureFlagName } from '../../shared/constants';

@Injectable({
    providedIn: 'root'
})
export class FeatureFlagService {
    private _featureFlags = new BehaviorSubject<FeatureFlag[]>([]);
    private _featureFlagsLoaded = false;

    constructor(private _featureFlagsDataService: FeatureFlagsDataService) { }

    async loadFeatureFlags(): Promise<void> {
        if (this._featureFlagsLoaded) {
            return;
        }

        const featureFlags = await firstValueFrom(
            this._featureFlagsDataService.getFeatureFlags().pipe(
                map((features: Feature) => features.featureFlags ?? []),
                catchError(() => of([] as FeatureFlag[]))
            )
        );

        this._featureFlags.next(featureFlags);
        this._featureFlagsLoaded = true;
    }

    getAllFeatures(): Observable<FeatureFlag[]> {
        if (!this._featureFlagsLoaded) {
            void this.loadFeatureFlags();
        }

        return this._featureFlags.asObservable();
    }

    isEnabled(flagName: FeatureFlagName): boolean {
        const flag = this._featureFlags.value.find(f => f.uniqueName === flagName);
        return flag?.enabled ?? false;
    }

    isDisabled(flagName: FeatureFlagName): boolean {
        return !this.isEnabled(flagName);
    }

    getFlag(flagName: FeatureFlagName): FeatureFlag | undefined {
        return this._featureFlags.value.find(f => f.uniqueName === flagName);
    }
}
